(function () {
  const AUTOPLAY_MS = 6000;
  const SCROLL_END_FALLBACK_MS = 700;

  function afterScrollEnd(track, callback) {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      callback();
    };

    if ('onscrollend' in track) {
      track.addEventListener('scrollend', finish, { once: true });
    }
    setTimeout(finish, SCROLL_END_FALLBACK_MS);
  }

  function makeClone(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-fw-hero-clone', 'true');
    clone.setAttribute('data-fw-hero-slide', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.removeAttribute('id');

    // Loop clones sit before/after real slides — demote every heading so they
    // never create H2-before-H1 (or duplicate IDs) in the document outline.
    clone.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      const div = document.createElement('div');
      div.className = heading.className;
      div.innerHTML = heading.innerHTML;
      heading.replaceWith(div);
    });

    clone.querySelectorAll('img').forEach((img) => {
      img.loading = 'lazy';
      img.removeAttribute('fetchpriority');
    });

    return clone;
  }

  const DRAG_THRESHOLD_PX = 50;

  function isHeroInteractiveTarget(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        'a, button, input, textarea, select, label, [role="button"], [role="tab"], .fw-hero__btn, .fw-hero__dot'
      )
    );
  }

  function initHero(root) {
    if (root.dataset.fwHeroLoop === 'true') return;

    const viewport = root.querySelector('.fw-hero__viewport');
    const track = root.querySelector('[data-fw-hero-track]');
    const dotsBar = root.querySelector('.fw-hero__dots');
    const dots = root.querySelectorAll('[data-fw-hero-dot]');
    const realSlides = [...track.querySelectorAll('[data-fw-hero-slide]:not([data-fw-hero-clone])')];
    const realCount = realSlides.length;
    if (!track || realCount < 1) return;

    root.dataset.fwHeroLoop = 'true';

    if (realCount < 2) {
      if (dotsBar) dotsBar.hidden = true;
      return;
    }

    const firstSlide = realSlides[0];
    const lastSlide = realSlides[realCount - 1];
    const startClone = makeClone(lastSlide);
    const endClone = makeClone(firstSlide);

    track.insertBefore(startClone, firstSlide);
    track.appendChild(endClone);

    let index = 0;
    let timer = null;
    let paused = false;
    let visible = true;
    let jumping = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function slideThreshold() {
      return (realSlides[0].offsetWidth || track.clientWidth) * 0.35;
    }

    const mobileDotsMq = window.matchMedia('(max-width: 767px)');

    function updateDots(realIndex) {
      const slide = realSlides[realIndex];
      root.classList.toggle('is-vr-active', Boolean(slide?.classList.contains('fw-hero__slide--vr')));

      dots.forEach((dot) => {
        const dotIndex = Number(dot.getAttribute('data-slide-index'));
        const active = dotIndex === realIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function syncMobileDotsPosition() {
      if (!dotsBar) return;

      if (mobileDotsMq.matches) {
        dotsBar.style.top = 'auto';
        dotsBar.style.bottom = '10px';
      } else {
        dotsBar.style.top = '';
        dotsBar.style.bottom = '';
      }
    }

    function scrollBehaviorFor(smooth) {
      return smooth && !reducedMotion ? 'smooth' : 'auto';
    }

    function instantJumpToReal(i) {
      jumping = true;
      const prevBehavior = track.style.scrollBehavior;
      const prevSnap = track.style.scrollSnapType;

      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollLeft = realSlides[i].offsetLeft;
      index = i;
      updateDots(i);
      syncMobileDotsPosition();

      requestAnimationFrame(() => {
        track.style.scrollBehavior = prevBehavior;
        track.style.scrollSnapType = prevSnap;
        jumping = false;
        syncMobileDotsPosition();
      });
    }

    function scrollToRealIndex(i, smooth) {
      const target = realSlides[i];
      if (!target) return;
      index = i;
      track.scrollTo({ left: target.offsetLeft, behavior: scrollBehaviorFor(smooth) });
      updateDots(index);
      syncMobileDotsPosition();
    }

    function scrollToEndClone(smooth) {
      track.scrollTo({ left: endClone.offsetLeft, behavior: scrollBehaviorFor(smooth) });
      updateDots(0);
    }

    function scrollToStartClone(smooth) {
      track.scrollTo({ left: startClone.offsetLeft, behavior: scrollBehaviorFor(smooth) });
      updateDots(realCount - 1);
    }

    function isOnEndClone() {
      return Math.abs(track.scrollLeft - endClone.offsetLeft) < slideThreshold();
    }

    function isOnStartClone() {
      return Math.abs(track.scrollLeft - startClone.offsetLeft) < slideThreshold();
    }

    function resetFromEndClone() {
      if (jumping) return;
      instantJumpToReal(0);
    }

    function resetFromStartClone() {
      if (jumping) return;
      instantJumpToReal(realCount - 1);
    }

    function handleCloneArrival() {
      if (jumping) return;
      if (isOnEndClone()) {
        afterScrollEnd(track, resetFromEndClone);
        return;
      }
      if (isOnStartClone()) {
        afterScrollEnd(track, resetFromStartClone);
      }
    }

    function advanceForward() {
      if (jumping) return;

      if (index < realCount - 1) {
        scrollToRealIndex(index + 1, true);
        return;
      }

      scrollToEndClone(true);
      afterScrollEnd(track, () => {
        if (isOnEndClone()) resetFromEndClone();
      });
    }

    function retreatBackward() {
      if (jumping) return;

      if (index > 0) {
        scrollToRealIndex(index - 1, true);
        return;
      }

      scrollToStartClone(true);
      afterScrollEnd(track, () => {
        if (isOnStartClone()) resetFromStartClone();
      });
    }

    function stopAutoplay() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (reducedMotion || paused || !visible || realCount < 2) return;
      timer = setInterval(advanceForward, AUTOPLAY_MS);
    }

    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';
    track.scrollLeft = firstSlide.offsetLeft;
    index = 0;
    updateDots(0);
    syncMobileDotsPosition();
    requestAnimationFrame(() => {
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
      syncMobileDotsPosition();
    });

    mobileDotsMq.addEventListener('change', syncMobileDotsPosition);
    window.addEventListener('resize', syncMobileDotsPosition);

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = Number(dot.getAttribute('data-slide-index'));
        if (Number.isNaN(i) || i < 0 || i >= realCount) return;
        scrollToRealIndex(i, true);
        startAutoplay();
      });
    });

    root.addEventListener('mouseenter', () => {
      paused = true;
      stopAutoplay();
    });
    root.addEventListener('mouseleave', () => {
      paused = false;
      startAutoplay();
    });
    root.addEventListener('focusin', () => {
      paused = true;
      stopAutoplay();
    });
    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) {
        paused = false;
        startAutoplay();
      }
    });

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) startAutoplay();
        else stopAutoplay();
      },
      { threshold: 0.25 }
    );
    io.observe(root);

    let scrollSyncTimer;
    track.addEventListener(
      'scroll',
      () => {
        if (jumping) return;

        clearTimeout(scrollSyncTimer);
        scrollSyncTimer = setTimeout(() => {
          if (isOnEndClone() || isOnStartClone()) {
            if (isOnStartClone()) updateDots(realCount - 1);
            handleCloneArrival();
            return;
          }

          const left = track.scrollLeft;
          let nearest = 0;
          let dist = Infinity;
          realSlides.forEach((slide, i) => {
            const d = Math.abs(slide.offsetLeft - left);
            if (d < dist) {
              dist = d;
              nearest = i;
            }
          });

          if (nearest !== index) {
            index = nearest;
            updateDots(index);
            syncMobileDotsPosition();
          }
        }, 80);
      },
      { passive: true }
    );

    root.querySelectorAll('img').forEach((img) => {
      img.setAttribute('draggable', 'false');
    });

    if (viewport) {
      viewport.addEventListener(
        'dragstart',
        (event) => {
          event.preventDefault();
        },
        true
      );

      let pointerDragging = false;
      let dragStartX = 0;
      let dragStartScrollLeft = 0;
      let dragDelta = 0;
      let suppressClick = false;
      let lastDragDelta = 0;
      let activePointerId = null;
      let prevScrollSnap = '';

      const endPointerDrag = (event) => {
        if (!pointerDragging || (event && event.pointerId !== activePointerId)) return;

        pointerDragging = false;
        activePointerId = null;
        viewport.classList.remove('is-dragging');
        track.style.scrollSnapType = prevScrollSnap;

        try {
          viewport.releasePointerCapture(event.pointerId);
        } catch {
          /* pointer already released */
        }

        const delta = dragDelta;
        dragDelta = 0;
        lastDragDelta = delta;

        if (Math.abs(delta) >= DRAG_THRESHOLD_PX) {
          suppressClick = true;
          if (delta < 0) advanceForward();
          else retreatBackward();
          startAutoplay();
          return;
        }

        if (Math.abs(delta) > 4) suppressClick = true;
        scrollToRealIndex(index, true);
        startAutoplay();
      };

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (isHeroInteractiveTarget(event.target)) return;

        pointerDragging = true;
        activePointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartScrollLeft = track.scrollLeft;
        dragDelta = 0;
        prevScrollSnap = track.style.scrollSnapType;
        track.style.scrollSnapType = 'none';
        viewport.classList.add('is-dragging');
        stopAutoplay();

        try {
          viewport.setPointerCapture(event.pointerId);
        } catch {
          /* ignore */
        }
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!pointerDragging || event.pointerId !== activePointerId) return;

        dragDelta = event.clientX - dragStartX;
        if (Math.abs(dragDelta) > 3) event.preventDefault();
        track.scrollLeft = dragStartScrollLeft - dragDelta;
      });

      viewport.addEventListener('pointerup', endPointerDrag);
      viewport.addEventListener('pointercancel', endPointerDrag);

      viewport.addEventListener(
        'click',
        (event) => {
          if (!suppressClick) return;
          if (event.target.closest('.fw-hero__btn')) {
            if (Math.abs(lastDragDelta) < DRAG_THRESHOLD_PX) {
              suppressClick = false;
              return;
            }
          }
          if (event.target.closest('a')) {
            event.preventDefault();
            event.stopPropagation();
          }
          suppressClick = false;
        },
        true
      );
    }

    startAutoplay();
  }

  function boot() {
    document.querySelectorAll('[data-fw-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const hero = event.target.querySelector('[data-fw-hero]');
    if (hero) initHero(hero);
  });
})();
